import { useState } from "react";
import { useCookieConsent } from "context/cookieConsent";

import { CookieToggle } from "./CookieToggle";

type CookieConsentSettingsProps = {
  onBack: () => void;
};

export const CookieConsentSettings = ({ onBack }: CookieConsentSettingsProps) => {
  const { preferences, updatePreferences, acceptAll } = useCookieConsent();

  const [localAnalytics, setLocalAnalytics] = useState(preferences?.analytics ?? false);
  const [localAdvertising, setLocalAdvertising] = useState(preferences?.advertising ?? false);

  const handleSave = () => {
    updatePreferences({
      analytics: localAnalytics,
      advertising: localAdvertising,
    });
  };

  const handleAcceptAll = () => {
    acceptAll();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-hidden bg-black/30 p-4 sm:items-center">
      <div className="max-h-full w-full max-w-4xl overflow-y-auto overscroll-contain rounded-lg bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100"
            aria-label="Wróć"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Ustawienia zaawansowane</h2>
        </div>

        <div className="mb-6 space-y-4 text-sm text-gray-700">
          <p>
            Poniżej możesz zmienić ustawienia plików cookies. Korzystanie przez nas i&nbsp;naszych partnerów
            z&nbsp;opcjonalnych plików cookie, w&nbsp;tym analitycznych i&nbsp;marketingowych, wymaga Twojej zgody.
            W&nbsp;ramach cookies marketingowych wydzieliliśmy dwie kategorie: reklamowe (związane z&nbsp;wyświetlaniem
            reklam w&nbsp;związku z&nbsp;korzystaniem ze&nbsp;stron internetowych) oraz mediów społecznościowych
            (pozwalające na&nbsp;docieranie do&nbsp;Ciebie ze&nbsp;spersonalizowaną reklamą w&nbsp;portalach
            społecznościowych). Więcej informacji o&nbsp;poszczególnych kategoriach plików cookie, które stosujemy
            w&nbsp;serwisie, znajdziesz poniżej. Pełna lista plików cookie z&nbsp;podziałem na&nbsp;kategorie dostępna
            jest{" "}
            <a
              href="https://synergia.librus.pl/polityka-prywatnosci/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              tutaj
            </a>
            . Jeśli chcesz zaakceptować wszystkie pliki cookie, kliknij „Włącz wszystkie i&nbsp;przejdź
            do&nbsp;serwisu”. Jeśli chcesz, żebyśmy stosowali tylko niezbędne pliki cookie, kliknij „Zapisz”. Aby
            zmienić preferencje plików cookie, przesuń suwak przy wybranej kategorii i&nbsp;zatwierdź przyciskiem
            „Zapisz”. Swoje preferencje możesz zmienić w&nbsp;dowolnym czasie poprzez łącze „Ustawienia prywatności”
            dostępne w&nbsp;stopce naszej strony internetowej lub w&nbsp;menu ustawień naszej aplikacji.
          </p>

          <p>
            Korzystanie z&nbsp;plików cookie wiąże się z&nbsp;przetwarzaniem danych osobowych dotyczących Twojej
            aktywności w&nbsp;serwisie. Szczegółowe informacje o&nbsp;sposobie, w&nbsp;jaki my i&nbsp;nasi partnerzy
            używamy plików cookie oraz przetwarzamy Twoje dane, a&nbsp;także o&nbsp;przysługujących Ci prawach,
            znajdziesz w&nbsp;naszej{" "}
            <a
              href="https://www.librus.pl/polityka-prywatnosci/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Polityce prywatności
            </a>
            .
          </p>
        </div>

        <div className="mb-6">
          <CookieToggle
            id="necessary"
            label="Niezbędne pliki cookie"
            description={
              "Niezbędne pliki cookie umożliwiają korzystanie z\u00A0usług dostępnych w\u00A0ramach serwisu. Są konieczne dla prawidłowego funkcjonowania naszej strony. Możemy je wykorzystywać np. do\u00A0uwierzytelniania użytkowników, zapamiętywania sesji logowania czy też w\u00A0celach związanych z\u00A0ustawieniem opcji prywatności."
            }
            checked={true}
            disabled={true}
            onChange={() => {}}
          />

          <CookieToggle
            id="analytics"
            label="Analityczne pliki cookie"
            description={
              "Analityczne pliki cookie umożliwiają zbieranie informacji o\u00A0sposobie korzystania z\u00A0serwisu, w\u00A0tym sprawdzenie liczby wizyt i\u00A0źródeł ruchu. Pomagają nam ustalić, które strony są bardziej, a\u00A0które mniej popularne, i\u00A0zrozumieć, jak użytkownicy poruszają się po\u00A0stronie. Dzięki temu możemy badać statystyki i\u00A0poprawiać wydajność serwisu. Informacje zbierane przez te pliki cookie są agregowane, nie mają więc na\u00A0celu ustalenia Twojej tożsamości. Jeśli nie zezwolisz na\u00A0stosowanie tych plików cookie, nie będziemy wiedzieć, kiedy odwiedziłeś nasz serwis i\u00A0jak z\u00A0niego korzystałeś."
            }
            checked={localAnalytics}
            onChange={setLocalAnalytics}
          />

          <CookieToggle
            id="advertising"
            label="Reklamowe pliki cookie"
            description={
              "Reklamowe pliki cookie pozwalają na\u00A0dopasowanie wyświetlanych treści reklamowych do\u00A0Twoich zainteresowań w\u00A0naszym serwisie lub na\u00A0innych stronach, które odwiedzasz. Mogą być instalowane przez naszych partnerów reklamowych za\u00A0pośrednictwem naszej strony internetowej. Na\u00A0podstawie informacji z\u00A0tych plików cookie i\u00A0aktywności w\u00A0innych serwisach jest budowany Twój profil zainteresowań. Brak zgody na\u00A0te pliki cookie nie oznacza, że reklamy nie będą Ci wyświetlane, nie będą one jednak dopasowane do\u00A0Twoich preferencji."
            }
            checked={localAdvertising}
            onChange={setLocalAdvertising}
          />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            onClick={handleSave}
            className="focus:ring-primary rounded-md border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            Zapisz
          </button>
          <button
            onClick={handleAcceptAll}
            className="bg-primary hover:bg-primary-dark focus:ring-primary rounded-md px-6 py-3 text-sm font-medium text-white transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            Włącz wszystkie i&nbsp;przejdź do&nbsp;systemu
          </button>
        </div>
      </div>
    </div>
  );
};
